// src/hooks/useCareerFlow.jsx

import {
  useEffect,
  useRef,
  useState,
} from "react";

import { supabase } from "../supabaseClient";
import { apiFetch } from "../utils/apiFetch";
import {
  getBlogAttributionForApi,
  trackAttributedEventOnce,
} from "../utils/blogAnalytics";


// =============================================================
// API
// =============================================================

export const API_BASE =
  process.env.REACT_APP_API_BASE ||
  "https://api.reignitespirit.com";


// =============================================================
// FLOW STEPS
// =============================================================

export const STEPS = {
  QUESTIONNAIRE: 1,
  CONSENT: 2,
  PAYMENT: 3,
  RESULTS: 4,
};


// =============================================================
// PAYMENT MODE
//
// temporary:
//   Pay It Forward $5
//   View My Report free
//
// standard:
//   existing $9.99 / $19.99 flow
// =============================================================
export const REPORT_PAYMENT_MODE =
  (
    process.env.REACT_APP_REPORT_PAYMENT_MODE ||
    "temporary"
  )
    .toString()
    .trim()
    .toLowerCase();

console.log(
  "[ENV] REACT_APP_REPORT_PAYMENT_MODE:",
  process.env.REACT_APP_REPORT_PAYMENT_MODE
);

console.log(
  "[ENV] FINAL REPORT_PAYMENT_MODE:",
  REPORT_PAYMENT_MODE
);


// =============================================================
// REPORT HEADER CLEANER
// =============================================================

const stripHeaderBlock = (text = "") => {
  const lines = text
    .replace(/\r\n/g, "\n")
    .split("\n");

  const out = [];

  const scanLimit =
    Math.min(
      lines.length,
      40
    );

  for (
    let i = 0;
    i < lines.length;
    i++
  ) {
    const raw =
      lines[i] || "";

    const t =
      raw
        .replace(/\*+/g, "")
        .trim();

    const isHeader =
      (
        i < scanLimit &&
        /^(AI Career Pivot Report|Strategic Career Transition Report)$/i.test(
          t
        )
      ) ||
      /^(Report Type|Prepared for|Location|Date|Consultant|Subject)\s*:/i.test(
        t
      );

    if (isHeader) {
      continue;
    }

    out.push(raw);
  }

  return out
    .join("\n")
    .replace(
      /\n{3,}/g,
      "\n\n"
    )
    .trim();
};


// =============================================================
// GUID HELPERS
// =============================================================

const isGuid = (value) =>
  typeof value === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value
  );


const normalizeAssessmentId =
  (value) => {
    if (!value) {
      return null;
    }

    if (
      value === "null" ||
      value === "undefined"
    ) {
      return null;
    }

    return isGuid(value)
      ? value
      : null;
  };


// =============================================================
// STORAGE KEYS
// =============================================================

const getAssessmentKey =
  (uid) =>
    `current_assessment_id_${uid}`;


const getStepKey =
  (uid) =>
    `dashboard_step_${uid}`;


const getConsentKey =
  (uid) =>
    `consentAccepted_${uid}`;


const getResultsLockKey =
  (uid) =>
    `results_lock_${uid}`;


// =============================================================
// RESULTS LOCK
// =============================================================

const isResultsLocked =
  (uid) =>
    localStorage.getItem(
      getResultsLockKey(uid)
    ) === "true";


const lockResults =
  (uid) => {
    if (!uid) {
      return;
    }

    localStorage.setItem(
      getResultsLockKey(uid),
      "true"
    );
  };


const unlockResults =
  (uid) => {
    if (!uid) {
      return;
    }

    localStorage.removeItem(
      getResultsLockKey(uid)
    );
  };


// =============================================================
// RESPONSE PARSER
// =============================================================

const safeJson =
  async (res) => {
    const raw =
      await res.text();

    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  };


// =============================================================
// SESSION
// =============================================================

const getValidToken =
  async () => {
    const {
      data: sessionData,
    } =
      await supabase.auth.getSession();

    const session =
      sessionData?.session;

    return {
      token:
        session?.access_token ||
        null,

      userId:
        session?.user?.id ||
        null,
    };
  };


// =============================================================
// NORMALIZE REPORT PLAN
//
// REPORT TIER:
//
// snapshot
// deep_audit
//
// "free" remains accepted only for backward compatibility.
// It must NOT become the authenticated user's account tier.
// =============================================================

const normalizePlan =
  (plan) => {
    const value =
      (plan || "")
        .toString()
        .trim()
        .toLowerCase();

    if (
      value === "free" ||
      value === "free_report" ||
      value === "free-report"
    ) {
      return "free";
    }

    if (
      value === "deep_audit" ||
      value === "deep-audit" ||
      value === "deep audit" ||
      value === "premium" ||
      value.includes("deep")
    ) {
      return "deep_audit";
    }

    return "snapshot";
  };


// =============================================================
// ACCOUNT REPORT PLAN
//
// Only these are valid account tiers.
// =============================================================

const normalizeAccountPlan =
  (plan) => {
    const normalized =
      normalizePlan(plan);

    return normalized ===
      "deep_audit"
      ? "deep_audit"
      : "snapshot";
  };


// =============================================================
// PAYMENT STATUS
// =============================================================

const normalizePaymentStatus =
  (status) =>
    (status || "")
      .toString()
      .trim()
      .toLowerCase();


const hasReportAccess =
  (status) => {
    const value =
      normalizePaymentStatus(
        status
      );

    return (
      value === "free" ||
      value === "paid" ||
      value === "bypassed"
    );
  };


// =============================================================
// MAIN HOOK
// =============================================================

export function useCareerFlow() {

  // ===========================================================
  // STATE
  // ===========================================================

  const [
    authUid,
    setAuthUid,
  ] =
    useState(
      () =>
        sessionStorage.getItem(
          "auth_uid"
        ) || null
    );


  // IMPORTANT:
  //
  // This is REPORT TIER.
  //
  // snapshot
  // deep_audit
  //
  // Never use "free" here in the new temporary flow.

  const [
    paymentPlan,
    setPaymentPlan,
  ] =
    useState("snapshot");


  const [
    step,
    setStep,
  ] =
    useState(
      STEPS.QUESTIONNAIRE
    );


  const [
    report,
    setReport,
  ] =
    useState("");


  const [
    isGeneratingReport,
    setIsGeneratingReport,
  ] =
    useState(false);


  const [
    resultsBooting,
    setResultsBooting,
  ] =
    useState(false);


  const [
    progressText,
    setProgressText,
  ] =
    useState(
      "Initializing AI analysis..."
    );


  const [
    progressPercent,
    setProgressPercent,
  ] =
    useState(0);


  const [
    paymentConfirmed,
    setPaymentConfirmed,
  ] =
    useState(false);


  const [
    consentOk,
    setConsentOk,
  ] =
    useState(false);

// ===========================================================
// ONE-TIME FREE REPORT STATUS
//
// Database/backend is authoritative.
// Browser storage is NOT used as the security check.
// ===========================================================

const [
  freeReportStatus,
  setFreeReportStatus,
] = useState({
  loaded: false,
  freeReportUsed: false,
  canGenerateFreeReport: true,
  assessmentId: null,
  reportReady: false,
  claimedAt: null,
  usedAt: null,
});
  // ===========================================================
  // DEBUG ACCOUNT PLAN
  // ===========================================================

  useEffect(
    () => {
      console.log(
        "[CareerFlow] CURRENT ACCOUNT PLAN:",
        paymentPlan
      );
    },
    [paymentPlan]
  );


  // ===========================================================
  // REFS
  // ===========================================================

  const pollTimerRef =
    useRef(null);


  const fakeProgressIntervalRef =
    useRef(null);

  // Assessment whose report was newly generated in THIS browser flow.
  // Existing saved reports must not count as a new conversion.
  const newlyGeneratedAssessmentRef = useRef(null);


  // ===========================================================
  // SET STEP + SAVE
  // ===========================================================

  const setStepAndPersist =
    (nextStep) => {
      setStep(
        nextStep
      );

      if (authUid) {
        localStorage.setItem(
          getStepKey(
            authUid
          ),
          String(
            nextStep
          )
        );
      }
    };


  // ===========================================================
  // PROGRESS
  // ===========================================================

  const stopFakeProgress =
    () => {
      if (
        fakeProgressIntervalRef.current
      ) {
        clearInterval(
          fakeProgressIntervalRef.current
        );

        fakeProgressIntervalRef.current =
          null;
      }
    };


  const startFakeProgress =
    () => {
      stopFakeProgress();

      const messages = [
        "Analyzing your responses 🧠",
        "Identifying career patterns 🔍",
        "Mapping future-proof roles 🚀",
        "Writing personalized guidance ✍️",
        "Finalizing your career report 📄",
      ];

      let index = 0;
      let percent = 5;

      setProgressText(
        "Initializing AI analysis..."
      );

      setProgressPercent(
        percent
      );

      fakeProgressIntervalRef.current =
        setInterval(
          () => {
            setProgressText(
              messages[
                index %
                  messages.length
              ]
            );

            percent +=
              Math.floor(
                Math.random() *
                  8
              ) + 5;

            if (
              percent >= 95
            ) {
              percent = 95;
            }

            setProgressPercent(
              percent
            );

            index++;
          },
          2500
        );
    };


  // ===========================================================
  // RESTORE ASSESSMENT ID
  //
  // Also handles LemonSqueezy redirect.
  // ===========================================================

  const restoreAssessmentId =
    async (uid) => {
      if (!uid) {
        return null;
      }

      const params =
        new URLSearchParams(
          window.location.search
        );

      const paid =
        params.get(
          "paid"
        ) === "true";

      const urlAssessmentRaw =
        params.get(
          "assessment_id"
        ) ||
        params.get(
          "assessmentId"
        ) ||
        params.get(
          "assessment"
        ) ||
        null;

      const urlAssessmentId =
        normalizeAssessmentId(
          urlAssessmentRaw
        );

      if (
        paid &&
        urlAssessmentId
      ) {
        localStorage.setItem(
          getAssessmentKey(
            uid
          ),
          urlAssessmentId
        );

        params.delete(
          "paid"
        );

        params.delete(
          "assessment_id"
        );

        params.delete(
          "assessmentId"
        );

        params.delete(
          "assessment"
        );

        const clean =
          `${window.location.pathname}` +
          (
            params.toString()
              ? `?${params.toString()}`
              : ""
          );

        window.history.replaceState(
          {},
          "",
          clean
        );

        return urlAssessmentId;
      }

      return normalizeAssessmentId(
        localStorage.getItem(
          getAssessmentKey(
            uid
          )
        )
      );
    };


  // ===========================================================
  // POLL REPORT
  // ===========================================================
// ===========================================================
// LOAD ONE-TIME FREE REPORT STATUS
//
// Backend endpoint:
//
// GET /api/career-report/free-report-status
//
// IMPORTANT:
// This is the authoritative check.
// ===========================================================

const loadFreeReportStatus =
  async () => {
    try {
      const response =
        await apiFetch(
          "/api/career-report/free-report-status",
          {
            method: "GET",
          }
        );

      const data =
        await safeJson(
          response
        );

      console.log(
        "[FREE REPORT STATUS]",
        response.status,
        data
      );

      if (!response.ok) {
        console.warn(
          "[FREE REPORT STATUS] Unable to load status:",
          data
        );

        return null;
      }

      const existingAssessmentId =
        normalizeAssessmentId(
          data?.assessmentId
        );

      const status = {
        loaded: true,

        freeReportUsed:
          data?.freeReportUsed ===
          true,

        canGenerateFreeReport:
          data?.canGenerateFreeReport !==
          false,

        assessmentId:
          existingAssessmentId,

        reportReady:
          data?.reportReady ===
          true,

        claimedAt:
          data?.freeReportClaimedAt ??
          null,

        usedAt:
          data?.freeReportUsedAt ??
          null,
      };

      setFreeReportStatus(
        status
      );

      return status;
    } catch (error) {
      console.error(
        "[FREE REPORT STATUS] FAILED:",
        error
      );

      return null;
    }
  };
  // ===========================================================
// POLL REPORT
//
// This ONLY reads an existing/generated report.
// It NEVER triggers Gemini.
// ===========================================================

const pollForCareerReport =
  (assessmentId) => {
    const normalizedId =
      normalizeAssessmentId(
        assessmentId
      );

    if (!normalizedId) {
      console.error(
        "[REPORT POLL] Invalid assessment ID:",
        assessmentId
      );

      stopFakeProgress();

      setResultsBooting(
        false
      );

      setIsGeneratingReport(
        false
      );

      return;
    }


    if (
      pollTimerRef.current
    ) {
      clearTimeout(
        pollTimerRef.current
      );
    }


    setResultsBooting(
      true
    );


    const startedUid =
      sessionStorage.getItem(
        "auth_uid"
      );


    const poll =
      async () => {
        // -----------------------------------------------------
        // Stop if another user logged into this browser tab.
        // -----------------------------------------------------

        if (
          sessionStorage.getItem(
            "auth_uid"
          ) !== startedUid
        ) {
          stopFakeProgress();

          setResultsBooting(
            false
          );

          setIsGeneratingReport(
            false
          );

          return;
        }


        try {
          const res =
            await apiFetch(
              `/api/career-report/report/by-assessment/${normalizedId}`,
              {
                method:
                  "GET",
              }
            );


          const raw =
            await res.text();


          // ===================================================
          // AUTH SESSION MAY STILL BE INITIALIZING
          // ===================================================

          if (
            res.status ===
            401
          ) {
            pollTimerRef.current =
              setTimeout(
                poll,
                800
              );

            return;
          }


          // ===================================================
          // FORBIDDEN
          //
          // Usually means wrong assessment ID.
          //
          // IMPORTANT:
          // Do NOT keep polling forever.
          // ===================================================

          if (
            res.status ===
            403
          ) {
            console.error(
              "[REPORT POLL] 403 Forbidden. Assessment:",
              normalizedId
            );

            stopFakeProgress();

            setResultsBooting(
              false
            );

            setIsGeneratingReport(
              false
            );

            if (
              pollTimerRef.current
            ) {
              clearTimeout(
                pollTimerRef.current
              );
            }

            pollTimerRef.current =
              null;

            return;
          }


          // ===================================================
          // NOT FOUND
          // ===================================================

          if (
            res.status ===
            404
          ) {
            console.error(
              "[REPORT POLL] Report assessment not found:",
              normalizedId
            );

            stopFakeProgress();

            setResultsBooting(
              false
            );

            setIsGeneratingReport(
              false
            );

            if (
              pollTimerRef.current
            ) {
              clearTimeout(
                pollTimerRef.current
              );
            }

            pollTimerRef.current =
              null;

            return;
          }


          // ===================================================
          // OTHER HARD CLIENT ERRORS
          // ===================================================

          if (
            res.status >= 400 &&
            res.status < 500
          ) {
            console.error(
              "[REPORT POLL] Client error:",
              res.status,
              raw
            );

            stopFakeProgress();

            setResultsBooting(
              false
            );

            setIsGeneratingReport(
              false
            );

            if (
              pollTimerRef.current
            ) {
              clearTimeout(
                pollTimerRef.current
              );
            }

            pollTimerRef.current =
              null;

            return;
          }


          // ===================================================
          // TEMPORARY SERVER ERROR
          //
          // Retry because report processing may still be
          // completing.
          // ===================================================

          if (
            !res.ok ||
            !raw
          ) {
            pollTimerRef.current =
              setTimeout(
                poll,
                2500
              );

            return;
          }


          let data;


          try {
            data =
              JSON.parse(
                raw
              );
          } catch {
            pollTimerRef.current =
              setTimeout(
                poll,
                2000
              );

            return;
          }


          // ===================================================
          // REPORT READY
          // ===================================================

          if (
            data?.report?.trim()
          ) {
            stopFakeProgress();

            setProgressPercent(
              100
            );


            setReport(
              stripHeaderBlock(
                data.report
              )
            );


            setResultsBooting(
              false
            );

            setIsGeneratingReport(
              false
            );


            if (
              pollTimerRef.current
            ) {
              clearTimeout(
                pollTimerRef.current
              );
            }


            pollTimerRef.current =
              null;


            if (authUid) {
              lockResults(
                authUid
              );
            }

            if (
              newlyGeneratedAssessmentRef.current === normalizedId
            ) {
              trackAttributedEventOnce(
                "report_generated",
                normalizedId,
                {
                  assessmentId: normalizedId,
                  reportType: paymentPlan,
                }
              );

              newlyGeneratedAssessmentRef.current = null;
            }

            return;
          }


          // ===================================================
          // REPORT RECORD EXISTS BUT CONTENT NOT READY
          // ===================================================

          pollTimerRef.current =
            setTimeout(
              poll,
              2500
            );
        } catch (error) {
          console.warn(
            "[CareerFlow] Poll failed:",
            error
          );

          pollTimerRef.current =
            setTimeout(
              poll,
              2500
            );
        }
      };


    poll();
  };

  // ===========================================================
// WAIT FOR PAID LEMON REPORT
//
// Lemon redirects the browser back to React before the
// webhook is guaranteed to have finished:
//
// payment
//   -> webhook
//   -> AI generation
//   -> report_text
//   -> PDF
//   -> email
//
// This function DOES NOT generate another report.
//
// It only waits for the report created by the webhook.
// ===========================================================

const waitForPaidReport =
  (assessmentId) => {
    const normalizedId =
      normalizeAssessmentId(
        assessmentId
      );


    if (!normalizedId) {
      console.error(
        "[LEMON RETURN] Invalid assessment ID:",
        assessmentId
      );


      stopFakeProgress();


      setResultsBooting(
        false
      );


      setIsGeneratingReport(
        false
      );


      return;
    }


    // -------------------------------------------------------
    // Stop any previous timer.
    // -------------------------------------------------------

    if (
      pollTimerRef.current
    ) {
      clearTimeout(
        pollTimerRef.current
      );

      pollTimerRef.current =
        null;
    }


    const startedAt =
      Date.now();


    // Maximum time we wait for webhook/report generation.
    const timeoutMs =
      120000;


    const check =
      async () => {
        try {
          console.log(
            "[LEMON RETURN] Checking paid report status:",
            normalizedId
          );


          // =================================================
          // CHECK AUTHORITATIVE BACKEND STATUS
          // =================================================

          const response =
            await apiFetch(
              `/api/careerpivot/assessment-status/${normalizedId}?t=${Date.now()}`,
              {
                method:
                  "GET",
              }
            );


          const data =
            await safeJson(
              response
            );


          console.log(
            "[LEMON RETURN] Status:",
            response.status,
            data
          );


          // =================================================
          // STATUS RESPONSE SUCCESS
          // =================================================

          if (
            response.ok
          ) {
            const paymentStatus =
              (
                data?.paymentStatus ||
                ""
              )
                .toString()
                .trim()
                .toLowerCase();


            const hasReport =
              data?.hasReport ===
              true;


            const webhookProcessed =
              data?.webhookProcessed ===
              true;


            const reportEmailSent =
              data?.reportEmailSent ===
              true;


            console.log(
              "[LEMON RETURN] Backend state:",
              {
                paymentStatus,
                hasReport,
                webhookProcessed,
                reportEmailSent,
              }
            );


            // ===============================================
            // PAYMENT VERIFIED
            // ===============================================

            if (
              paymentStatus ===
              "paid"
            ) {
              setPaymentConfirmed(
                true
              );


              if (
                !hasReport
              ) {
                setProgressText(
                  "Payment confirmed. Preparing your AI career report..."
                );
              }
            }


            // ===============================================
            // REPORT IS READY
            // ===============================================

            if (
              hasReport
            ) {
              console.log(
                "[LEMON RETURN] REPORT READY:",
                normalizedId
              );


              stopFakeProgress();


              setProgressPercent(
                95
              );


              setProgressText(
                "Your career report is ready. Loading it now..."
              );


              setResultsBooting(
                true
              );


              // ------------------------------------------------
              // IMPORTANT
              //
              // We are no longer generating anything here.
              //
              // The webhook already generated the report.
              // ------------------------------------------------

              setIsGeneratingReport(
                false
              );


              // ===============================================
              // READ THE EXISTING REPORT
              //
              // NO POST /generate
              // NO SECOND GEMINI CALL
              // ===============================================

              const pendingPaymentChoice =
                localStorage.getItem("reignite_pending_payment_choice") ||
                "standard_purchase";

              const pendingReportType =
                localStorage.getItem("reignite_pending_report_type") ||
                paymentPlan;

              trackAttributedEventOnce(
                "paid_conversion_from_blog",
                normalizedId,
                {
                  assessmentId: normalizedId,
                  reportType: pendingReportType,
                  paymentChoice: pendingPaymentChoice,
                  paymentMode: REPORT_PAYMENT_MODE,
                }
              );

              trackAttributedEventOnce(
                "report_generated",
                `paid_${normalizedId}`,
                {
                  assessmentId: normalizedId,
                  reportType: pendingReportType,
                }
              );

              localStorage.removeItem("reignite_pending_payment_choice");
              localStorage.removeItem("reignite_pending_report_type");

              pollForCareerReport(
                normalizedId
              );


              return;
            }
          }


          // =================================================
          // TIMEOUT
          // =================================================

          if (
            Date.now() -
              startedAt >=
            timeoutMs
          ) {
            console.error(
              "[LEMON RETURN] Timed out waiting for paid report:",
              normalizedId
            );


            stopFakeProgress();


            setResultsBooting(
              false
            );


            setIsGeneratingReport(
              false
            );


            setProgressPercent(
              100
            );


            setProgressText(
              "Your payment was received. Your report is still being processed. Please refresh this page shortly."
            );


            if (
              pollTimerRef.current
            ) {
              clearTimeout(
                pollTimerRef.current
              );

              pollTimerRef.current =
                null;
            }


            return;
          }


          // =================================================
          // KEEP WAITING
          // =================================================

          pollTimerRef.current =
            window.setTimeout(
              check,
              2000
            );
        } catch (error) {
          console.error(
            "[LEMON RETURN] Status polling failed:",
            error
          );


          // -------------------------------------------------
          // Retry temporary failure while within timeout.
          // -------------------------------------------------

          if (
            Date.now() -
              startedAt <
            timeoutMs
          ) {
            pollTimerRef.current =
              window.setTimeout(
                check,
                2500
              );


            return;
          }


          stopFakeProgress();


          setResultsBooting(
            false
          );


          setIsGeneratingReport(
            false
          );


          setProgressText(
            "Unable to confirm your paid report. Please refresh the page shortly."
          );
        }
      };


    // Start immediately.
    check();
  };


  // ===========================================================
  // DB USER PLAN
  //
  // AUTHORITATIVE SOURCE:
  //
  // users.is_premium = false
  // -> snapshot
  //
  // users.is_premium = true
  // -> deep_audit
  // ===========================================================

  const getPlanFromDbTruth =
    async (
      currentAuthUid
    ) => {
      try {
        if (
          !currentAuthUid
        ) {
          return null;
        }

        const res =
          await apiFetch(
            "/api/users/oauth-status",
            {
              method: "POST",

              headers: {
                "Content-Type":
                  "application/json",
              },

              body:
                JSON.stringify({
                  authUid:
                    currentAuthUid,
                }),
            }
          );

        const status =
          await safeJson(
            res
          );

        console.log(
          "[CareerFlow] oauth-status:",
          status
        );

        if (!res.ok) {
          console.error(
            "[CareerFlow] Unable to load account plan:",
            status
          );

          return null;
        }

        // ASP.NET may serialize as:
        //
        // isPremium
        //
        // or backend may return:
        //
        // is_premium

        const premiumValue =
          status?.isPremium ??
          status?.is_premium ??
          false;

        const isPremium =
          premiumValue === true ||
          premiumValue === "true" ||
          premiumValue === 1 ||
          premiumValue === "1";

        const accountPlan =
          isPremium
            ? "deep_audit"
            : "snapshot";

        console.log(
          "[CareerFlow] DB isPremium:",
          isPremium
        );

        console.log(
          "[CareerFlow] DB ACCOUNT PLAN:",
          accountPlan
        );

        return accountPlan;
      } catch (error) {
        console.error(
          "[CareerFlow] getPlanFromDbTruth failed:",
          error
        );

        return null;
      }
    };


  // ===========================================================
  // STORAGE PLAN
  //
  // FALLBACK ONLY.
  //
  // Database always wins when available.
  // ===========================================================

  const getUserPlan =
    (uid) => {
      const tabPlan =
        (
          sessionStorage.getItem(
            "user_plan"
          ) || ""
        )
          .toLowerCase();

      const userPlan =
        (
          uid
            ? localStorage.getItem(
                `user_plan_${uid}`
              )
            : ""
        ) || "";

      const selected =
        (
          localStorage.getItem(
            "selectedPlan"
          ) || ""
        )
          .toLowerCase();

      const value =
        (
          tabPlan ||
          userPlan ||
          selected ||
          "snapshot"
        )
          .toLowerCase();

      return normalizeAccountPlan(
        value
      );
    };


  // ===========================================================
  // SAVE ACCOUNT PLAN LOCALLY
  //
  // Only snapshot/deep_audit can be stored here.
  // ===========================================================

  const syncAccountPlanStorage =
    (
      uid,
      plan
    ) => {
      if (!uid) {
        return;
      }

      const accountPlan =
        normalizeAccountPlan(
          plan
        );

      sessionStorage.setItem(
        "user_plan",
        accountPlan
      );

      localStorage.setItem(
        `user_plan_${uid}`,
        accountPlan
      );

      localStorage.setItem(
        "selectedPlan",
        accountPlan
      );
    };


  // ===========================================================
  // GET CURRENT AUTHORITATIVE ACCOUNT PLAN
  // ===========================================================

  const resolveCurrentAccountPlan =
    async (
      currentUserId
    ) => {
      const dbPlan =
        await getPlanFromDbTruth(
          currentUserId
        );

      if (
        dbPlan === "snapshot" ||
        dbPlan === "deep_audit"
      ) {
        setPaymentPlan(
          dbPlan
        );

        syncAccountPlanStorage(
          currentUserId,
          dbPlan
        );

        return dbPlan;
      }

      const fallback =
        normalizeAccountPlan(
          paymentPlan ||
            getUserPlan(
              currentUserId
            )
        );

      setPaymentPlan(
        fallback
      );

      return fallback;
    };


  // ===========================================================
  // AUTH UID SYNC
  // ===========================================================

  useEffect(
    () => {
      const sync =
        async () => {
          const {
            userId,
          } =
            await getValidToken();

          if (userId) {
            sessionStorage.setItem(
              "auth_uid",
              userId
            );

            setAuthUid(
              userId
            );
          }
        };

      sync();
    },
    []
  );


  // ===========================================================
  // INITIAL ACCOUNT PLAN
  //
  // IMPORTANT:
  //
  // DATABASE WINS.
  //
  // This fixes stale Deep Audit browser storage overriding
  // is_premium = false.
  // ===========================================================

  useEffect(
    () => {
      if (!authUid) {
        return;
      }

      let cancelled =
        false;

      const run =
        async () => {
          const dbPlan =
            await getPlanFromDbTruth(
              authUid
            );

          if (cancelled) {
            return;
          }

          if (
            dbPlan ===
              "snapshot" ||
            dbPlan ===
              "deep_audit"
          ) {
            console.log(
              "[CareerFlow] Using DB account plan:",
              dbPlan
            );

            setPaymentPlan(
              dbPlan
            );

            syncAccountPlanStorage(
              authUid,
              dbPlan
            );

            return;
          }

          // Backend unavailable:
          // use browser plan only as fallback.

          const storedPlan =
            getUserPlan(
              authUid
            );

          const fallbackPlan =
            normalizeAccountPlan(
              storedPlan
            );

          console.warn(
            "[CareerFlow] DB plan unavailable. Using storage fallback:",
            fallbackPlan
          );

          setPaymentPlan(
            fallbackPlan
          );
        };

      run();

      return () => {
        cancelled = true;
      };

      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [authUid]
  );


  // ===========================================================
  // CONSENT STATE
  // ===========================================================

  useEffect(
    () => {
      if (!authUid) {
        return;
      }

      setConsentOk(
        localStorage.getItem(
          getConsentKey(
            authUid
          )
        ) === "true"
      );
    },
    [
      authUid,
      step,
    ]
  );


  // ===========================================================
  // CONSENT ACCEPTED
  // ===========================================================

  const handleConsentAccepted =
    () => {
      if (!authUid) {
        return;
      }

      localStorage.setItem(
        getConsentKey(
          authUid
        ),
        "true"
      );

      setConsentOk(
        true
      );

      setStep(
        STEPS.PAYMENT
      );

      localStorage.setItem(
        getStepKey(
          authUid
        ),
        String(
          STEPS.PAYMENT
        )
      );
    };

// ===========================================================
// LOAD FREE REPORT STATUS WHEN REPORT OPTIONS OPENS
// ===========================================================

useEffect(
  () => {
    if (!authUid) {
      return;
    }

    if (
      REPORT_PAYMENT_MODE !==
      "temporary"
    ) {
      return;
    }

    if (
      step !==
      STEPS.PAYMENT
    ) {
      return;
    }

    loadFreeReportStatus();
  },

  // loadFreeReportStatus intentionally uses current auth state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [
    authUid,
    step,
  ]
);
  // ===========================================================
// ===========================================================
// BOOT / RESTORE
//
// CRITICAL:
//
// Lemon payment return MUST be handled BEFORE:
//
// restoreAssessmentId()
//
// because restoreAssessmentId() currently removes:
//
// paid=true
// assessment_id=...
//
// from the browser URL.
//
// Correct flow:
//
// Lemon payment
//      ↓
// React return URL
//      ↓
// detect paid=true
//      ↓
// Results screen
//      ↓
// wait for webhook
//      ↓
// existing report is loaded
//
// NO duplicate Gemini generation.
// ===========================================================

useEffect(
  () => {
    if (
      !authUid
    ) {
      return;
    }


    const boot =
      async () => {
        // =====================================================
        // 1. DETECT RETURN FROM LEMON SQUEEZY
        //
        // MUST happen before restoreAssessmentId().
        // =====================================================

        const params =
          new URLSearchParams(
            window.location.search
          );


        const paidReturn =
          params.get(
            "paid"
          ) === "true";


        const paymentAssessmentId =
          normalizeAssessmentId(
            params.get(
              "assessment_id"
            ) ||
            params.get(
              "assessmentId"
            ) ||
            params.get(
              "assessment"
            )
          );


        // =====================================================
        // 2. SPECIAL PAID RETURN FLOW
        // =====================================================

        if (
          paidReturn &&
          paymentAssessmentId
        ) {
          console.log(
            "=============================================="
          );

          console.log(
            "[LEMON RETURN] SUCCESSFUL PAYMENT RETURN"
          );

          console.log(
            "[LEMON RETURN] User:",
            authUid
          );

          console.log(
            "[LEMON RETURN] Assessment:",
            paymentAssessmentId
          );

          console.log(
            "=============================================="
          );


          // ---------------------------------------------------
          // Make the paid assessment the active assessment.
          // ---------------------------------------------------

          localStorage.setItem(
            getAssessmentKey(
              authUid
            ),
            paymentAssessmentId
          );


          // ---------------------------------------------------
          // Consent was already accepted before checkout.
          //
          // Do NOT send paid user back to consent.
          // ---------------------------------------------------

          localStorage.setItem(
            getConsentKey(
              authUid
            ),
            "true"
          );


          setConsentOk(
            true
          );


          // ---------------------------------------------------
          // Go directly to Results.
          // ---------------------------------------------------

          setStep(
            STEPS.RESULTS
          );


          localStorage.setItem(
            getStepKey(
              authUid
            ),
            String(
              STEPS.RESULTS
            )
          );


          // ---------------------------------------------------
          // Payment succeeded in Lemon.
          //
          // Backend/webhook remains authoritative for DB status.
          // ---------------------------------------------------

          setPaymentConfirmed(
            true
          );


          // ---------------------------------------------------
          // Prepare Results screen.
          // ---------------------------------------------------

          setReport(
            ""
          );


          setResultsBooting(
            true
          );


          setIsGeneratingReport(
            true
          );


          setProgressPercent(
            10
          );


          setProgressText(
            "Payment received. Preparing your AI career report..."
          );


          startFakeProgress();


          // ===================================================
          // 3. CLEAN PAYMENT RETURN URL
          //
          // IMPORTANT:
          //
          // Only clean AFTER assessment ID and state are saved.
          // ===================================================

          params.delete(
            "paid"
          );


          params.delete(
            "assessment_id"
          );


          params.delete(
            "assessmentId"
          );


          params.delete(
            "assessment"
          );


          const cleanUrl =
            window.location.pathname +
            (
              params.toString()
                ? `?${params.toString()}`
                : ""
            );


          window.history.replaceState(
            {},
            "",
            cleanUrl
          );


          // ===================================================
          // 4. WAIT FOR WEBHOOK-GENERATED REPORT
          //
          // DO NOT:
          //
          // POST /api/career-report/generate
          //
          // The Lemon webhook is responsible for generation.
          // ===================================================

          waitForPaidReport(
            paymentAssessmentId
          );


          // CRITICAL:
          //
          // Do not continue into normal boot logic.
          return;
        }


        // =====================================================
        // 5. NORMAL APPLICATION BOOT
        //
        // Not returning from Lemon.
        // =====================================================

        const assessmentId =
          await restoreAssessmentId(
            authUid
          );


        // =====================================================
        // 6. EXISTING FINISHED RESULT
        // =====================================================

        if (
          isResultsLocked(
            authUid
          )
        ) {
          setStep(
            STEPS.RESULTS
          );


          localStorage.setItem(
            getStepKey(
              authUid
            ),
            String(
              STEPS.RESULTS
            )
          );


          if (
            !report?.trim() &&
            assessmentId
          ) {
            setResultsBooting(
              true
            );


            // Existing report is loading,
            // NOT generating.

            setIsGeneratingReport(
              false
            );


            setProgressText(
              "Loading your existing career report..."
            );


            setProgressPercent(
              30
            );


            pollForCareerReport(
              assessmentId
            );
          }


          return;
        }


        // =====================================================
        // 7. NO ASSESSMENT
        // =====================================================

        if (
          !assessmentId
        ) {
          setStep(
            STEPS.QUESTIONNAIRE
          );


          localStorage.setItem(
            getStepKey(
              authUid
            ),
            String(
              STEPS.QUESTIONNAIRE
            )
          );


          return;
        }


        // =====================================================
        // 8. VERIFY SESSION
        // =====================================================

        const {
          token,
        } =
          await getValidToken();


        if (
          !token
        ) {
          setStep(
            STEPS.QUESTIONNAIRE
          );


          return;
        }


        // =====================================================
        // 9. CHECK QUESTIONNAIRE COMPLETION FIRST
        //
        // CRITICAL:
        //
        // An assessment ID only means an assessment exists.
        // It does NOT mean the questionnaire was completed.
        //
        // On refresh, an in-progress questionnaire must stay on
        // STEPS.QUESTIONNAIRE. Consent is allowed only after the
        // backend explicitly reports questionnaireStatus=completed.
        // =====================================================

        let questionnaireStatus =
          "";

        try {
          const draftRes =
            await apiFetch(
              `/api/careerpivot/get-draft/${assessmentId}`,
              {
                method:
                  "GET",
              }
            );

          const draftData =
            await safeJson(
              draftRes
            );

          questionnaireStatus =
            (
              draftData?.questionnaireStatus ||
              draftData?.questionnaire_status ||
              ""
            )
              .toString()
              .trim()
              .toLowerCase();

          console.log(
            "[CareerFlow BOOT] Questionnaire status:",
            {
              assessmentId,
              status:
                questionnaireStatus,
              responseStatus:
                draftRes.status,
            }
          );

          // ---------------------------------------------------
          // SAFE DEFAULT:
          //
          // Unless the backend explicitly confirms "completed",
          // this assessment is still treated as questionnaire
          // work-in-progress.
          // ---------------------------------------------------

          if (
            !draftRes.ok ||
            questionnaireStatus !==
              "completed"
          ) {
            console.log(
              "[CareerFlow BOOT] Questionnaire is not completed. Resume questionnaire.",
              {
                assessmentId,
                questionnaireStatus,
              }
            );

            setStep(
              STEPS.QUESTIONNAIRE
            );

            localStorage.setItem(
              getStepKey(
                authUid
              ),
              String(
                STEPS.QUESTIONNAIRE
              )
            );

            return;
          }
        } catch (error) {
          console.warn(
            "[CareerFlow BOOT] Unable to verify questionnaire completion. Staying on questionnaire:",
            error?.message ||
              error
          );

          setStep(
            STEPS.QUESTIONNAIRE
          );

          localStorage.setItem(
            getStepKey(
              authUid
            ),
            String(
              STEPS.QUESTIONNAIRE
            )
          );

          return;
        }


        // =====================================================
        // 10. CHECK EXISTING REPORT
        //
        // Only questionnaires confirmed as COMPLETED reach here.
        // =====================================================

        const res =
          await apiFetch(
            `/api/career-report/report/by-assessment/${assessmentId}`,
            {
              method:
                "GET",
            }
          );


        const data =
          await safeJson(
            res
          );


        console.log(
          "[CareerFlow BOOT] Existing report response:",
          res.status,
          data
        );


        // =====================================================
        // 10. NO REPORT ACCESS
        //
        // This is NORMAL navigation only.
        //
        // Lemon payment return does NOT reach here anymore.
        // =====================================================

        if (
          !res.ok
        ) {
          const targetStep =
            consentOk
              ? STEPS.PAYMENT
              : STEPS.CONSENT;


          setStep(
            targetStep
          );


          localStorage.setItem(
            getStepKey(
              authUid
            ),
            String(
              targetStep
            )
          );


          return;
        }


        // =====================================================
        // 11. PAYMENT / REPORT ACCESS
        // =====================================================

        const paymentStatus =
          normalizePaymentStatus(
            data?.paymentStatus
          );


        const reportAccess =
          hasReportAccess(
            paymentStatus
          );


        const isPaid =
          paymentStatus ===
          "paid";


        setPaymentConfirmed(
          isPaid
        );


        // =====================================================
        // 12. REPORT ACCESS EXISTS
        // =====================================================

        if (
          reportAccess
        ) {
          setStep(
            STEPS.RESULTS
          );


          localStorage.setItem(
            getStepKey(
              authUid
            ),
            String(
              STEPS.RESULTS
            )
          );


          // ===================================================
          // REPORT CONTENT ALREADY RETURNED
          // ===================================================

          if (
            data?.report?.trim()
          ) {
            setReport(
              stripHeaderBlock(
                data.report
              )
            );


            lockResults(
              authUid
            );


            setResultsBooting(
              false
            );


            setIsGeneratingReport(
              false
            );


            setProgressPercent(
              100
            );


            stopFakeProgress();


            return;
          }


          // ===================================================
          // ACCESS EXISTS BUT CONTENT STILL BEING PREPARED
          // ===================================================

          setResultsBooting(
            true
          );


          setIsGeneratingReport(
            false
          );


          setProgressText(
            "Loading your career report..."
          );


          setProgressPercent(
            40
          );


          pollForCareerReport(
            assessmentId
          );


          return;
        }


        // =====================================================
        // 13. NORMAL FALLBACK
        // =====================================================

        const targetStep =
          consentOk
            ? STEPS.PAYMENT
            : STEPS.CONSENT;


        setStep(
          targetStep
        );


        localStorage.setItem(
          getStepKey(
            authUid
          ),
          String(
            targetStep
          )
        );
      };


    boot();


    // =========================================================
    // CLEANUP
    // =========================================================

    return () => {
      if (
        pollTimerRef.current
      ) {
        clearTimeout(
          pollTimerRef.current
        );


        pollTimerRef.current =
          null;
      }


      stopFakeProgress();
    };


    // eslint-disable-next-line react-hooks/exhaustive-deps
  },
  [
    authUid,
  ]
);

  // ===========================================================
  // TEMPORARY FLOW:
  // VIEW MY REPORT
  //
  // No payment.
  //
  // Snapshot account:
  //   Snapshot report FREE
  //
  // Deep Audit account:
  //   Deep Audit report FREE
  //
  // IMPORTANT:
  //
  // paymentPlan remains snapshot/deep_audit.
  //
  // "free" is only paymentChoice.
  // ===========================================================
// ===========================================================
// VIEW EXISTING FREE REPORT
//
// Absolutely NO Gemini generation.
// Absolutely NO POST /career-report/generate.
// ===========================================================

const handleViewExistingReport =
  async (
    assessmentIdOverride =
      null
  ) => {
    try {
      const {
        data: sessionData,
      } =
        await supabase.auth
          .getSession();


      const session =
        sessionData?.session;


      const currentUserId =
        session?.user?.id;


      if (!currentUserId) {
        alert(
          "Please login again."
        );

        return;
      }


      // -------------------------------------------------------
      // Prefer explicitly supplied assessment ID.
      // Otherwise refresh status from backend.
      // -------------------------------------------------------

      let existingAssessmentId =
        normalizeAssessmentId(
          assessmentIdOverride
        );


      if (
        !existingAssessmentId
      ) {
        const status =
          await loadFreeReportStatus();


        existingAssessmentId =
          normalizeAssessmentId(
            status?.assessmentId
          );
      }


      if (
        !existingAssessmentId
      ) {
        alert(
          "Your existing career report could not be located."
        );

        return;
      }


      console.log(
        "[EXISTING REPORT] Loading assessment:",
        existingAssessmentId
      );


      // -------------------------------------------------------
      // THIS IS NOW THE CURRENT REPORT ASSESSMENT.
      // -------------------------------------------------------

      localStorage.setItem(
        getAssessmentKey(
          currentUserId
        ),
        existingAssessmentId
      );


      setStep(
        STEPS.RESULTS
      );


      localStorage.setItem(
        getStepKey(
          currentUserId
        ),
        String(
          STEPS.RESULTS
        )
      );


      setReport("");


      setProgressText(
        "Loading your existing career report..."
      );


      setProgressPercent(
        30
      );


      setResultsBooting(
        true
      );


      // We are loading, not generating.
      setIsGeneratingReport(
        false
      );


      // Do NOT call:
      //
      // /api/career-report/generate
      //
      // Simply fetch the existing report.

      pollForCareerReport(
        existingAssessmentId
      );
    } catch (error) {
      console.error(
        "[EXISTING REPORT] FAILED:",
        error
      );

      stopFakeProgress();

      setResultsBooting(
        false
      );

      setIsGeneratingReport(
        false
      );

      alert(
        error?.message ||
          "Unable to load your existing career report."
      );
    }
  };
  // ===========================================================
// TEMPORARY FLOW:
// VIEW MY REPORT
//
// FIRST free report:
//   Generates ONCE.
//
// Later clicks:
//   Load existing report.
//
// Report tier:
//   Snapshot / Deep Audit from users.is_premium.
//
// "free" is payment choice only.
// ===========================================================

const handleViewReport =
  async () => {
    try {
      console.log(
        "===== VIEW MY REPORT CLICKED ====="
      );


      const {
        data: sessionData,
      } =
        await supabase.auth
          .getSession();


      const session =
        sessionData?.session;


      const token =
        session?.access_token;


      const currentUserId =
        session?.user?.id;


      if (
        !token ||
        !currentUserId
      ) {
        alert(
          "Please login again."
        );

        return;
      }


      // =====================================================
      // 1. FIRST CHECK SERVER-SIDE FREE REPORT STATUS
      //
      // This prevents even sending /generate when the
      // user already owns an existing report.
      // =====================================================

      const existingStatus =
        await loadFreeReportStatus();


      if (
        existingStatus
          ?.freeReportUsed ===
          true &&
        existingStatus
          ?.assessmentId
      ) {
        console.log(
          "[VIEW REPORT] Free report already used."
        );

        console.log(
          "[VIEW REPORT] Loading existing assessment:",
          existingStatus
            .assessmentId
        );


        await handleViewExistingReport(
          existingStatus
            .assessmentId
        );


        return;
      }


      // =====================================================
      // 2. CURRENT QUESTIONNAIRE ASSESSMENT
      // =====================================================

      const assessmentId =
        await restoreAssessmentId(
          currentUserId
        );


      console.log(
        "[VIEW REPORT] Current assessmentId:",
        assessmentId
      );


      if (!assessmentId) {
        alert(
          "Please complete the questionnaire first."
        );

        return;
      }


      // =====================================================
      // 3. AUTHORITATIVE ACCOUNT REPORT TIER
      // =====================================================

      const reportPlan =
        await resolveCurrentAccountPlan(
          currentUserId
        );


      if (
        reportPlan !==
          "snapshot" &&
        reportPlan !==
          "deep_audit"
      ) {
        alert(
          "Unable to determine your report plan."
        );

        return;
      }


      console.log(
        "[VIEW REPORT] account plan:",
        reportPlan
      );


      console.log(
        "[VIEW REPORT] paymentChoice: free"
      );


      setPaymentPlan(
        reportPlan
      );


      syncAccountPlanStorage(
        currentUserId,
        reportPlan
      );


      // =====================================================
      // 4. FIRST GENERATION UI
      //
      // Only now do we show AI generation because server
      // says this user has NOT consumed the free report.
      // =====================================================

      unlockResults(
        currentUserId
      );


      setStep(
        STEPS.RESULTS
      );


      localStorage.setItem(
        getStepKey(
          currentUserId
        ),
        String(
          STEPS.RESULTS
        )
      );


      setReport("");


      setPaymentConfirmed(
        false
      );


      setResultsBooting(
        true
      );


      setIsGeneratingReport(
        true
      );


      setProgressText(
        "Initializing AI analysis..."
      );


      startFakeProgress();


      // =====================================================
      // 5. GENERATE FIRST FREE REPORT
      // =====================================================

      const payload = {
        assessmentId,

        plan:
          reportPlan,

        paymentChoice:
          "free",

        sendEmail:
          true,
      };


      console.log(
        "[VIEW REPORT] POST /api/career-report/generate",
        payload
      );


      const response =
        await apiFetch(
          "/api/career-report/generate",
          {
            method:
              "POST",

            headers: {
              "Content-Type":
                "application/json",
            },

            body:
              JSON.stringify(
                payload
              ),
          }
        );


      const data =
        await safeJson(
          response
        );


      console.log(
        "[VIEW REPORT] response:",
        response.status,
        data
      );


      // =====================================================
      // 6. BACKEND SAYS ALREADY CLAIMED / USED
      //
      // Race condition protection:
      // another tab/request may have claimed first.
      // =====================================================

      if (!response.ok) {
        stopFakeProgress();

        setResultsBooting(
          false
        );

        setIsGeneratingReport(
          false
        );


        if (
          response.status ===
            409 &&
          data?.assessmentId
        ) {
          const existingAssessmentId =
            normalizeAssessmentId(
              data.assessmentId
            );


          if (
            existingAssessmentId
          ) {
            console.log(
              "[VIEW REPORT] Backend returned existing assessment:",
              existingAssessmentId
            );


            await handleViewExistingReport(
              existingAssessmentId
            );


            return;
          }
        }


        setStep(
          STEPS.PAYMENT
        );


        localStorage.setItem(
          getStepKey(
            currentUserId
          ),
          String(
            STEPS.PAYMENT
          )
        );


        alert(
          typeof data ===
            "string"
            ? data
            : data?.message ||
                data?.detail ||
                "Report generation failed."
        );


        return;
      }


      // =====================================================
      // 7. USE THE ASSESSMENT ID RETURNED BY BACKEND
      //
      // VERY IMPORTANT.
      //
      // Backend may return OLD assessment ID when report
      // already existed.
      // =====================================================

      const returnedAssessmentId =
        normalizeAssessmentId(
          data?.assessmentId
        );


      const reportAssessmentId =
        returnedAssessmentId ||
        assessmentId;


      // Persist the correct report assessment.
      localStorage.setItem(
        getAssessmentKey(
          currentUserId
        ),
        reportAssessmentId
      );


      // =====================================================
      // 8. EXISTING REPORT RETURNED
      //
      // NO MORE GENERATION.
      // =====================================================

      if (
        data?.alreadyGenerated ===
        true
      ) {
        console.log(
          "[VIEW REPORT] Existing report returned by backend:",
          reportAssessmentId
        );


        stopFakeProgress();


        setProgressText(
          "Loading your existing career report..."
        );


        setProgressPercent(
          50
        );


        setIsGeneratingReport(
          false
        );


        // Refresh status so PaymentStep will display
        // "View Existing Report" next time.
        await loadFreeReportStatus();


        pollForCareerReport(
          reportAssessmentId
        );


        return;
      }


      // =====================================================
      // 9. FIRST REPORT GENERATED
      // =====================================================

      console.log(
        "[VIEW REPORT] First free report generated:",
        reportAssessmentId
      );

      newlyGeneratedAssessmentRef.current = reportAssessmentId;

      await loadFreeReportStatus();


      pollForCareerReport(
        reportAssessmentId
      );
    } catch (error) {
      console.error(
        "[VIEW REPORT] FAILED:",
        error
      );


      stopFakeProgress();


      setResultsBooting(
        false
      );


      setIsGeneratingReport(
        false
      );


      alert(
        error?.message ||
          "Unable to generate your report."
      );
    }
  };

  // ===========================================================
  // BACKWARD-COMPATIBLE FREE HANDLER
  //
  // Some old components may still call this function.
  //
  // It now means:
  //
  // View My Report without payment.
  //
  // It does NOT set account plan to free.
  // ===========================================================

  const handleGenerateFreeReport =
    async () => {
      console.log(
        "[CareerFlow] Legacy free handler -> View My Report"
      );

      await handleViewReport();
    };


  // ===========================================================
  // TEMPORARY FLOW:
  // PAY IT FORWARD - US$5
  //
  // User voluntarily pays $5.
  //
  // Report tier remains:
  //
  // snapshot
  // OR
  // deep_audit
  //
  // Payment must NOT upgrade/downgrade the report.
  // ===========================================================

  const handlePayItForward =
    async () => {
      try {
        console.log(
          "===== PAY IT FORWARD CLICKED ====="
        );

        const {
          data: sessionData,
        } =
          await supabase.auth.getSession();

        const session =
          sessionData?.session;

        const token =
          session?.access_token;

        const currentUserId =
          session?.user?.id;

        if (
          !token ||
          !currentUserId
        ) {
          alert(
            "Please login again."
          );

          return;
        }

        // -----------------------------------------------------
        // Assessment
        // -----------------------------------------------------

        const assessmentId =
          await restoreAssessmentId(
            currentUserId
          );

        if (
          !assessmentId
        ) {
          alert(
            "Please complete the questionnaire first."
          );

          return;
        }

        // -----------------------------------------------------
        // Account plan
        // -----------------------------------------------------

        const reportPlan =
          await resolveCurrentAccountPlan(
            currentUserId
          );

        if (
          reportPlan !==
            "snapshot" &&
          reportPlan !==
            "deep_audit"
        ) {
          alert(
            "Unable to determine your report plan."
          );

          return;
        }

        console.log(
          "[PAY IT FORWARD] account plan:",
          reportPlan
        );

        setPaymentPlan(
          reportPlan
        );

        syncAccountPlanStorage(
          currentUserId,
          reportPlan
        );

        // -----------------------------------------------------
        // TEMPORARY $5 PAYMENT
        // -----------------------------------------------------

        const payload = {
          assessmentId,

          plan:
            reportPlan,

          paymentChoice:
            "pay_it_forward",

          ...getBlogAttributionForApi(),
        };

        console.log(
          "[PAY IT FORWARD] POST /api/careerpivot/start-payment",
          payload
        );

        const response =
          await apiFetch(
            "/api/careerpivot/start-payment",
            {
              method:
                "POST",

              headers: {
                "Content-Type":
                  "application/json",
              },

              body:
                JSON.stringify(
                  payload
                ),
            }
          );

        const data =
          await safeJson(
            response
          );

        console.log(
          "[PAY IT FORWARD] response:",
          response.status,
          data
        );

        if (
          !response.ok
        ) {
          alert(
            typeof data ===
              "string"
              ? data
              : data?.message ||
                data?.detail ||
                "Unable to start Pay It Forward."
          );

          return;
        }

        const checkoutUrl =
          data?.checkoutUrl;

        if (
          !checkoutUrl
        ) {
          alert(
            "Payment checkout URL was not returned."
          );

          return;
        }

        localStorage.setItem("reignite_pending_payment_choice", "pay_it_forward");
        localStorage.setItem("reignite_pending_report_type", reportPlan);

        trackAttributedEventOnce(
          "payment_started",
          `pay_it_forward_${assessmentId}`,
          {
            assessmentId,
            reportType: reportPlan,
            paymentChoice: "pay_it_forward",
            paymentMode: REPORT_PAYMENT_MODE,
          }
        );

        window.location.href =
          checkoutUrl;
      } catch (error) {
        console.error(
          "[PAY IT FORWARD] FAILED:",
          error
        );

        alert(
          error?.message ||
            "Unable to start Pay It Forward."
        );
      }
    };


  // ===========================================================
  // ORIGINAL STANDARD PAID CHECKOUT
  //
  // KEEP THIS FUNCTION.
  //
  // It is required when:
  //
  // REACT_APP_REPORT_PAYMENT_MODE=standard
  //
  // Snapshot:
  // $9.99
  //
  // Deep Audit:
  // $19.99
  // ===========================================================

  const handleStartPayment =
    async (
      planOrEvent
    ) => {
      const selectedPlan =
        normalizePlan(
          typeof planOrEvent ===
            "string"
            ? planOrEvent
            : paymentPlan
        );

      console.log(
        "===== STANDARD REPORT PLAN SELECTED =====",
        selectedPlan
      );

      // -------------------------------------------------------
      // Backward compatibility:
      //
      // Old components that still send "free"
      // are redirected to the new free-access handler.
      // -------------------------------------------------------

      if (
        selectedPlan ===
        "free"
      ) {
        console.log(
          "[CareerFlow] FREE -> View My Report"
        );

        await handleViewReport();

        return;
      }

      if (
        selectedPlan !==
          "snapshot" &&
        selectedPlan !==
          "deep_audit"
      ) {
        alert(
          "Invalid paid report plan."
        );

        return;
      }

      try {
        const {
          data: sessionData,
        } =
          await supabase.auth.getSession();

        const session =
          sessionData?.session;

        const token =
          session?.access_token;

        const currentUserId =
          session?.user?.id;

        if (
          !token ||
          !currentUserId
        ) {
          alert(
            "Your session is still loading. Please try again."
          );

          return;
        }

        const assessmentId =
          await restoreAssessmentId(
            currentUserId
          );

        if (
          !assessmentId
        ) {
          alert(
            "Please complete the questionnaire first."
          );

          return;
        }

        // Standard flow preserves its existing behavior.

        setPaymentPlan(
          selectedPlan
        );

        syncAccountPlanStorage(
          currentUserId,
          selectedPlan
        );

        const payload = {
          assessmentId,

          plan:
            selectedPlan,

          paymentChoice:
            "standard_purchase",

          ...getBlogAttributionForApi(),
        };

        console.log(
          "[STANDARD PAYMENT] POST /api/careerpivot/start-payment",
          payload
        );

        const response =
          await apiFetch(
            "/api/careerpivot/start-payment",
            {
              method:
                "POST",

              headers: {
                "Content-Type":
                  "application/json",
              },

              body:
                JSON.stringify(
                  payload
                ),
            }
          );

        const data =
          await safeJson(
            response
          );

        if (
          !response.ok
        ) {
          alert(
            typeof data ===
              "string"
              ? data
              : data?.message ||
                "Unable to start payment."
          );

          return;
        }

        const checkoutUrl =
          data?.checkoutUrl;

        if (
          !checkoutUrl
        ) {
          alert(
            "Missing LemonSqueezy checkout URL."
          );

          return;
        }

        localStorage.setItem("reignite_pending_payment_choice", "standard_purchase");
        localStorage.setItem("reignite_pending_report_type", selectedPlan);

        trackAttributedEventOnce(
          "payment_started",
          `standard_${assessmentId}`,
          {
            assessmentId,
            reportType: selectedPlan,
            paymentChoice: "standard_purchase",
            paymentMode: REPORT_PAYMENT_MODE,
          }
        );

        window.location.href =
          checkoutUrl;
      } catch (error) {
        console.error(
          "handleStartPayment FAILED:",
          error
        );

        alert(
          error?.message ||
            "Unable to start payment."
        );
      }
    };


  // ===========================================================
  // SET PLAN + OPEN REPORT OPTIONS
  //
  // Existing helper retained.
  // ===========================================================

  const setPaymentPlanAndGoPayment =
    (plan) => {
      const normalized =
        normalizeAccountPlan(
          plan
        );

      setPaymentPlan(
        normalized
      );

      if (authUid) {
        syncAccountPlanStorage(
            authUid,
          normalized
        );
      }

      setStep(
        STEPS.PAYMENT
      );

      if (authUid) {
        localStorage.setItem(
          getStepKey(
            authUid
          ),
          String(
            STEPS.PAYMENT
          )
        );
      }
    };


  // ===========================================================
  // GO QUESTIONNAIRE
  // ===========================================================

  const goToQuestionnaire =
    () => {
      setStep(
        STEPS.QUESTIONNAIRE
      );

      if (authUid) {
        unlockResults(
          authUid
        );

        localStorage.setItem(
          getStepKey(
            authUid
          ),
          String(
            STEPS.QUESTIONNAIRE
          )
        );
      }
    };


  // ===========================================================
  // RETURN
  // ===========================================================

 // ===========================================================
// RETURN
// ===========================================================

return {
  STEPS,

  REPORT_PAYMENT_MODE,

  step,
  setStep,

  report,

  isGeneratingReport,
  resultsBooting,

  progressText,
  progressPercent,

  paymentConfirmed,
  consentOk,

  // =========================================================
  // USER ACCOUNT REPORT TIER
  //
  // users.is_premium = false -> snapshot
  // users.is_premium = true  -> deep_audit
  // =========================================================

  paymentPlan,

  setPaymentPlanAndGoPayment,

  handleConsentAccepted,

  // =========================================================
  // ONE-TIME FREE REPORT
  //
  // Backend / database is authoritative.
  //
  // These are used by PaymentStep / QuestionnairePage
  // to decide whether to show:
  //
  // View My Report
  //
  // OR
  //
  // View Existing Report
  // =========================================================

  freeReportStatus,

  loadFreeReportStatus,

  handleViewExistingReport,

  // =========================================================
  // TEMPORARY FLOW
  // =========================================================

  // Free, first-time report generation.
  //
  // Before POST /generate, this checks:
  //
  // GET /api/career-report/free-report-status
  //
  // If already generated, it loads the old report instead.
  handleViewReport,

  // Optional $5 contribution.
  //
  // This does NOT change Snapshot / Deep Audit entitlement.
  handlePayItForward,

  // Backward compatibility for old components.
  //
  // Internally redirects to handleViewReport().
  handleGenerateFreeReport,

  // =========================================================
  // ORIGINAL STANDARD PAID FLOW
  //
  // Existing:
  // Snapshot  -> $9.99
  // Deep Audit -> $19.99
  //
  // Keep this available for
  // REACT_APP_REPORT_PAYMENT_MODE=standard
  // =========================================================

  handleStartPayment,

  goToQuestionnaire,

  setStepAndPersist,
};
}